import { IS_PREVIEW } from "../lib/theme";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { X, Map, Check, Square, CheckSquare } from "lucide-react";
import { getRoadmap, completeRoadmapItem, updateRoadmapItem } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { useRoadmapView } from "../lib/useRoadmapView";
import { roadmapPageLabel } from "../lib/roadmapPages";
import { audiencesOf } from "./RoadmapAudience";

const ROMAN = ["", "I", "II", "III", "IV", "V", "VI", "VII"];
const GRADE_COLORS = { 0: "#64748B", 1: "#EF4444", 2: "#F97316", 3: "#F59E0B", 4: "#EAB308", 5: "#84CC16", 6: "#22C55E", 7: "#14B8A6" };
const autoGrade = (it) => { const inc = (it.tasks || []).filter((t) => !t.done && t.grade >= 1 && t.grade <= 7); return inc.length ? Math.min(...inc.map((t) => t.grade)) : 0; };
const effGrade = (it) => (it.grade >= 1 && it.grade <= 7 ? it.grade : autoGrade(it));

// Surfaces roadmap items linked to the CURRENT page as a blurred in-page overlay
// (no navigation). Only active when the owner's "Roadmap View" toggle is on.
export function RoadmapPageOverlay() {
    const { isAdmin } = useAuth();
    const roadmapView = useRoadmapView();
    const { pathname } = useLocation();
    const qc = useQueryClient();
    const enabled = roadmapView && isAdmin && !IS_PREVIEW;
    const { data: allItems = [] } = useQuery({ queryKey: ["roadmap-all", isAdmin], queryFn: () => getRoadmap(), enabled });

    const items = useMemo(() => allItems.filter((it) => !it.completed && (it.linked_page || "").split("?")[0] === pathname), [allItems, pathname]);

    const [open, setOpen] = useState(false);
    const shownFor = useRef("");
    // Auto-open once per page visit when there are linked items.
    useEffect(() => {
        if (!enabled) { setOpen(false); return; }
        if (items.length && shownFor.current !== pathname) { setOpen(true); shownFor.current = pathname; }
        if (!items.length) setOpen(false);
    }, [enabled, items.length, pathname]);

    if (!enabled || items.length === 0) return null;

    const toggleTask = (item, taskId) => {
        const tasks = (item.tasks || []).map((t) => (t.id === taskId ? { ...t, done: !t.done } : t));
        const auds = audiencesOf(item);
        updateRoadmapItem(item.id, { title: item.title, info: item.info || "", audiences: auds, is_public: auds.includes("public"), visibility: auds[0], alert_enabled: !!item.alert_enabled, alert_date: item.alert_date || "", grade: item.grade || 0, tasks, linked_page: item.linked_page || "" })
            .then(() => qc.invalidateQueries({ queryKey: ["roadmap-all"] }));
    };
    const markComplete = (item) => completeRoadmapItem(item.id, true).then(() => qc.invalidateQueries({ queryKey: ["roadmap-all"] }));

    const label = roadmapPageLabel(pathname) || "This Page";

    return (
        <>
            {/* Floating launcher pill (always available to reopen) */}
            {!open && (
                <button type="button" data-testid="roadmap-overlay-pill" onClick={() => setOpen(true)}
                    className="fixed bottom-5 right-5 z-[120] inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#9D4CDD] text-white shadow-2xl shadow-black/40 hover:bg-[#B366F0] transition-colors">
                    <Map className="w-4 h-4" />
                    <span className="text-xs font-black uppercase tracking-wider">Roadmap · {items.length}</span>
                </button>
            )}

            {open && (
                <div className="fixed inset-0 z-[120] flex items-start justify-center p-4 sm:p-8 overflow-y-auto" data-testid="roadmap-overlay">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={() => setOpen(false)} />
                    <div className="relative w-full max-w-2xl rounded-2xl border border-[#9D4CDD]/40 bg-[#0C0E17] shadow-2xl my-4">
                        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-white/10 sticky top-0 bg-[#0C0E17] rounded-t-2xl z-10">
                            <div className="flex items-center gap-2 min-w-0">
                                <Map className="w-5 h-5 text-[#B366F0] shrink-0" />
                                <div className="min-w-0">
                                    <h2 className="text-base font-extrabold text-white leading-tight truncate">Roadmap — {label}</h2>
                                    <p className="text-[11px] text-white/40">{items.length} open item{items.length === 1 ? "" : "s"} linked to this page</p>
                                </div>
                            </div>
                            <button type="button" onClick={() => setOpen(false)} data-testid="roadmap-overlay-close" className="p-1.5 text-white/50 hover:text-white shrink-0"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
                            {items.map((it) => {
                                const eg = effGrade(it);
                                const c = eg ? GRADE_COLORS[eg] : "#9D4CDD";
                                return (
                                    <div key={it.id} className="relative rounded-xl border border-white/10 bg-[#11141F] overflow-hidden" data-testid={`roadmap-overlay-item-${it.id}`}>
                                        <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: c }} />
                                        <div className="p-4 pl-5">
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="min-w-0 flex-1">
                                                    {eg >= 1 && <span className="inline-block text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full mb-1.5" style={{ background: `${c}22`, color: c, border: `1px solid ${c}59` }}>Grade {ROMAN[eg]}</span>}
                                                    <h3 className="text-base font-extrabold text-white leading-tight break-words">{it.title}</h3>
                                                    {it.info && <p className="mt-1 text-sm text-white/60 leading-relaxed whitespace-pre-line break-words">{it.info}</p>}
                                                    {(it.tasks || []).length > 0 && (
                                                        <ul className="mt-2 space-y-1">
                                                            {it.tasks.map((t) => {
                                                                const tc = GRADE_COLORS[t.grade] || GRADE_COLORS[0];
                                                                return (
                                                                    <li key={t.id}>
                                                                        <button type="button" onClick={() => toggleTask(it, t.id)} data-testid={`roadmap-overlay-task-${it.id}-${t.id}`} className="w-full flex items-center gap-2 text-left rounded-md px-1.5 py-1 hover:bg-white/5">
                                                                            {t.done ? <CheckSquare className="w-3.5 h-3.5 shrink-0 text-[#22C55E]" /> : <Square className="w-3.5 h-3.5 shrink-0 text-white/30" />}
                                                                            <span className="inline-flex items-center justify-center text-[9px] font-black w-5 shrink-0 rounded" style={{ color: tc, background: `${tc}22` }}>{ROMAN[t.grade]}</span>
                                                                            <span className={`text-[12px] leading-tight break-words ${t.done ? "text-white/35 line-through" : "text-white/75"}`}>{t.text || "Untitled task"}</span>
                                                                        </button>
                                                                    </li>
                                                                );
                                                            })}
                                                        </ul>
                                                    )}
                                                </div>
                                                <button type="button" onClick={() => markComplete(it)} data-testid={`roadmap-overlay-complete-${it.id}`} title="Mark complete" className="p-1.5 text-white/40 hover:text-[#22C55E] shrink-0"><Check className="w-4 h-4" /></button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default RoadmapPageOverlay;
